import Image from "next/image";
import Link from "next/link";
import React, { type PropsWithChildren } from "react";
import { twMerge } from "tailwind-merge";

import * as Accordion from "@radix-ui/react-accordion";

import { getLinkTarget } from "@/globals";
import { BUTTON_STYLES } from "@/styles/buttons";
import { TYPO_STYLES } from "@/styles/typography";

export type AccordionItemProps = {
    button?: {
        href: string | undefined;
        label: string;
    };
    headline?: string;
    icon?: string;
    value: string;
};

export const AccordionItemView = ({
    button,
    headline,
    icon,
    value,
    children,
}: PropsWithChildren<AccordionItemProps>) => (
    <AccordionItem value={value} icon={icon}>
        <AccordionTrigger>{headline}</AccordionTrigger>
        <AccordionContent>
            <div className="[&_a]:underline">{children}</div>
            {button?.href && (
                <Link
                    className={twMerge(BUTTON_STYLES.plain(), "self-start nounderline")}
                    href={button.href}
                    target={getLinkTarget(button.href)}
                    prefetch={false}
                >
                    {button.label}
                    <span className={twMerge(TYPO_STYLES.icon(), "text-2xl font-light")}>
                        {String.fromCharCode(Number("0xe938"))}
                    </span>
                </Link>
            )}
        </AccordionContent>
    </AccordionItem>
);

type AccordionItemProp = {
    icon?: string;
    value: string;
};

const AccordionItem = ({ children, icon, value }: PropsWithChildren<AccordionItemProp>) => (
    <Accordion.Item
        className={twMerge("overflow-hidden content-center min-h-16 py-2 pl-1 bg-transparent")}
        value={value}
    >
        <div className="flex gap-4">
            {icon && (
                <div className="relative">
                    <Image src="/polygon_for_icon.svg" width={50} height={50} alt="icon" />
                    <span
                        className={twMerge(
                            TYPO_STYLES.icon(),
                            "text-3xl absolute top-[10px] left-[7px]",
                        )}
                    >
                        {String.fromCharCode(Number(`0x${icon}`))}
                    </span>
                </div>
            )}
            <div className="flex flex-col justify-center w-full">{children}</div>
        </div>
    </Accordion.Item>
);

const AccordionTrigger = ({ children }: PropsWithChildren) => (
    <Accordion.Header className="flex">
        <Accordion.Trigger
            className={twMerge(
                "group flex flex-1 cursor-pointer items-center justify-between text-brandDark text-xl md:text-2xl font-extrabold hover:text-greyDark gap-3",
            )}
        >
            <p className="text-left">{children}</p>
            <span
                className={twMerge(
                    TYPO_STYLES.icon(),
                    "text-3xl transition-transform duration-300 ease-[cubic-bezier(0.87,_0,_0.13,_1)] group-data-[state=open]:hidden",
                )}
                aria-hidden
            >
                {String.fromCharCode(Number("0xe901"))}
            </span>
            <span
                className={twMerge(
                    TYPO_STYLES.icon(),
                    "text-3xl transition-transform duration-300 ease-[cubic-bezier(0.87,_0,_0.13,_1)] hidden group-data-[state=open]:block",
                )}
                aria-hidden
            >
                {String.fromCharCode(Number("0xe956"))}
            </span>
        </Accordion.Trigger>
    </Accordion.Header>
);

const AccordionContent = ({ children }: PropsWithChildren) => (
    <Accordion.Content
        className={twMerge(
            "overflow-hidden data-[state=closed]:animate-slideUp data-[state=open]:animate-slideDown text-brandDark text-lg font-light",
        )}
    >
        <div className="py-1 text-left pr-6 md:pr-12 flex flex-col gap-2 ">{children}</div>
    </Accordion.Content>
);
